import { G } from '@mobily/ts-belt';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Typography } from '@mui/material';
import { Decimal } from 'decimal.js';
import { useEffect } from 'react';
import { required, SelectInput, useTranslate } from 'react-admin';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { getAllowedDeductibilityForPlanType } from '@Common/bo/b2b_plan_deductibility';
import { isFullyManagedPlan } from '@Common/types/b2b/plans';

import { ShowSection } from '@sh4res/bo-common/components';
import { voluntaryPaymentDeductibilityChoices } from '@sh4res/bo-common/components/B2bVoluntaryPayment/choices';
import { InstrumentSelectInput } from '@sh4res/bo-common/components/B2bVoluntaryPayment/components/InstrumentSelectInput';
import {
  PlanSelectChoice,
  PlanSelectInput,
} from '@sh4res/bo-common/components/B2bVoluntaryPayment/components/PlanSelectInput';
import { useOpenVoluntaryPaymentContractIds } from '@sh4res/bo-common/components/B2bVoluntaryPayment/useOpenVoluntaryPaymentContractIds';
import { AmountInput } from '@sh4res/bo-common/fields';
import { formatCentsNumber, minValueCents } from '@sh4res/bo-common/utils';

type AmountRow = {
  saving_plan_id: number | null;
  instrument_id: number | null;
  amount: number | null;
  plan_data: PlanSelectChoice | null;
  deductibility: string | null;
};

const testId = 'BoExternalB2bVoluntaryPaymentCreateAmountsStep';

export const AmountsStep = () => {
  const { control, setValue } = useFormContext();
  const translate = useTranslate();

  const [companyId, contractIds] = useWatch({
    name: ['b2b_company_id', 'contract_ids'],
  });
  const { openContractIds, allAffiliationsTerminated } =
    useOpenVoluntaryPaymentContractIds(contractIds);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rows',
  });

  useEffect(() => {
    if (fields.length === 0) {
      append({
        saving_plan_id: null,
        instrument_id: null,
        amount: null,
        plan_data: null,
        deductibility: null,
      });
    }
  }, [fields.length, append]);

  const handlePlanChange = (index: number, plan: PlanSelectChoice | undefined) => {
    setValue(`rows.${index}.plan_data`, plan ?? null);
    setValue(`rows.${index}.instrument_id`, null);
    setValue(`rows.${index}.amount`, null);
    setValue(`rows.${index}.deductibility`, null);
  };

  const rows: AmountRow[] = useWatch({ name: 'rows' }) ?? [];

  const totalAmount = rows.reduce((total, row) => total.add(row.amount ?? 0), new Decimal(0));

  return (
    <ShowSection leftAligned label="" noCard={true}>
      <Box sx={{ display: 'flex' }}>
        <Typography variant="subtitle1">
          {translate('resources.bo_common.b2b.voluntary_payment_create.fields.total_invested')}:{' '}
          {formatCentsNumber(totalAmount.toNumber())}
        </Typography>
      </Box>
      {allAffiliationsTerminated && (
        <Typography color="text.secondary" data-testid={`${testId}_NoActiveContract`}>
          {translate(
            'resources.bo_common.b2b.voluntary_payment_create.messages.no_plans_terminated_contract',
          )}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {fields.map((field, index) => {
          const rowData = rows[index] ?? {};
          const planData = rowData.plan_data;
          const isManaged = planData ? isFullyManagedPlan(planData.type) : false;
          const isPerecoi = planData?.isPerecoi;

          const allowedDeductibilities = planData
            ? getAllowedDeductibilityForPlanType(planData.type)
            : [];
          const deductibilityChoices = voluntaryPaymentDeductibilityChoices.filter((choice) =>
            allowedDeductibilities.includes(choice.id),
          );

          return (
            <Box key={field.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ width: '300px', display: 'flex', flexDirection: 'column' }}>
                <PlanSelectInput
                  data-testid={`${testId}_PlanSelectInput`}
                  source={`rows.${index}.saving_plan_id`}
                  label="resources.bo_common.b2b.voluntary_payment_create.fields.saving_plan_id"
                  filter={{ company_id: companyId, b2b_contract_id: openContractIds }}
                  validate={[required()]}
                  onPlanChange={(plan) => handlePlanChange(index, plan)}
                  sx={{ width: '100%', marginBottom: 0 }}
                  disablePlan={(choice) => {
                    if (!isFullyManagedPlan(choice.type)) {
                      return false;
                    }
                    return rows.some(
                      (row: AmountRow, rIndex: number) =>
                        rIndex !== index && row.saving_plan_id === choice.id,
                    );
                  }}
                />
              </Box>

              <Box sx={{ width: '180px', display: 'flex', flexDirection: 'column' }}>
                {isPerecoi && (
                  <SelectInput
                    data-testid={`${testId}_DeductibilitySelectInput`}
                    source={`rows.${index}.deductibility`}
                    label="resources.bo_common.b2b.voluntary_payment_create.fields.deductibility"
                    choices={deductibilityChoices}
                    validate={[required()]}
                    sx={{ width: '100%', marginBottom: 0 }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  width: '300px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}>
                {G.isNotNullable(planData) &&
                  (isManaged ? (
                    <Typography
                      sx={{ textAlign: 'center', paddingTop: '10px' }}
                      color="text.secondary">
                      {translate(
                        'resources.bo_common.b2b.campaigns.sections.allocations.risk_profile_current',
                      )}
                    </Typography>
                  ) : (
                    <InstrumentSelectInput
                      data-testid={`${testId}_InstrumentSelectInput`}
                      source={`rows.${index}.instrument_id`}
                      instrumentIds={planData.instruments_universe}
                      isCashSourcesPerInstrument={planData.is_cash_sources_per_instrument}
                      instrumentsCashSourcesLibre={planData.instruments_cash_sources_libre}
                      label="resources.bo_common.b2b.voluntary_payment_create.fields.instrument"
                      validate={[required()]}
                      sx={{ width: '100%', marginBottom: 0 }}
                    />
                  ))}
              </Box>

              <Box sx={{ width: '150px', display: 'flex', flexDirection: 'column' }}>
                {G.isNotNullable(planData) && (
                  <AmountInput
                    testId={`${testId}_AmountInput`}
                    source={`rows.${index}.amount`}
                    label="resources.bo_common.b2b.voluntary_payment_create.fields.amount"
                    validate={[required(), minValueCents(1000)]}
                    sx={{ width: '100%', marginBottom: 0 }}
                    InputProps={{
                      endAdornment: <Typography sx={{ pl: 1 }}>€</Typography>,
                    }}
                  />
                )}
              </Box>

              <IconButton
                data-testid={`${testId}_RemoveAllocationButton`}
                onClick={() => remove(index)}
                sx={{ mt: 1 }}>
                <DeleteIcon />
              </IconButton>
            </Box>
          );
        })}
        <Box>
          <IconButton
            data-testid={`${testId}_AddAllocationButton`}
            onClick={() =>
              append({
                saving_plan_id: null,
                instrument_id: null,
                amount: null,
                plan_data: null,
                deductibility: null,
              })
            }>
            <AddIcon />
            <Typography variant="body2" sx={{ ml: 1 }}>
              {translate('resources.bo_common.b2b.voluntary_payment_create.actions.add_allocation')}
            </Typography>
          </IconButton>
        </Box>
      </Box>
    </ShowSection>
  );
};
